import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, COUNTRIES, fmtINR } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import UniversityImage from "@/components/UniversityImage";
import { Search, Lock } from "lucide-react";
import { startGoogleLogin } from "@/lib/login";
import usePageMeta from "@/lib/usePageMeta";

const DEMO_SLUG = "university-of-melbourne";

export default function Universities() {
  usePageMeta({ title: "Universities abroad for Indian students", description: "Browse 80 universities across Australia, the UK, the US, Europe, Canada, Singapore and New Zealand, with tuition in rupees, rankings and post-study work rights.", path: "/universities" });
  const nav = useNavigate();
  const { user } = useAuth();
  const [country, setCountry] = useState("");
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);

  const startGoogle = () => {
    startGoogleLogin("/dashboard");
  };

  useEffect(() => {
    (async () => {
      const params = {};
      if (country) params.country = country;
      if (q) params.q = q;
      // Fetch the universities list and the cached og:image status map in
      // parallel. The status map (slug -> bool) lets us float universities
      // with a live campus image to the top; the rest keep their original
      // relative order. The endpoint is cache-only and never triggers a fresh
      // scrape — so this stays a single fast Mongo read.
      const [unisRes, statusRes] = await Promise.all([
        api.get("/universities", { params }),
        api.get("/universities/og-images-status").catch(() => ({ data: {} })),
      ]);
      const status = statusRes.data || {};
      const sorted = [...unisRes.data].sort((a, b) => {
        const ai = status[a.slug] ? 0 : 1;
        const bi = status[b.slug] ? 0 : 1;
        return ai - bi;
      });
      setItems(sorted);
    })();
  }, [country, q]);

  return (
    <div className="min-h-screen bg-white lg:pl-[240px]">
      <Nav />
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-12">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-micro font-semibold uppercase tracking-[0.05em] text-[var(--s500)]">University Atlas</span>
        </div>
        <h1 className="mt-3 text-h1 text-[var(--s900)]" data-testid="atlas-headline">
          Eighty universities, <em className="italic text-[var(--ind)]">one transparent map.</em>
        </h1>
        <p className="text-body text-[var(--s600)] mt-3 max-w-xl leading-relaxed">
          Eighty curated institutions across eleven countries — at least five per country. Verified public data, plainly labelled estimates.
        </p>

        {/* Country filter — all 11 countries in a single wrapping row.
            Flags render as real SVG images (flagcdn.com) instead of regional
            indicator emoji, which don't display on Windows/Linux desktop
            browsers without color-emoji fonts. */}
        <div className="mt-8">
          <div className="flex flex-wrap items-center gap-2">
            <Pill testid="atlas-all" active={country === ""} onClick={() => setCountry("")}>All</Pill>
            {COUNTRIES.map(c => (
              <Pill key={c.code} testid={`atlas-c-${c.code}`} active={country === c.code} onClick={() => setCountry(c.code)}>
                <FlagIcon code={c.code} /> {c.name}
              </Pill>
            ))}
            <div className="ml-auto relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--s400)]" strokeWidth={2} />
              <input
                className="w-64 pl-9"
                placeholder="Search by name…"
                value={q}
                onChange={e => setQ(e.target.value)}
                data-testid="atlas-search"
              />
            </div>
          </div>
        </div>

        {/* Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mt-8">
          {items.map(u => {
            const locked = !user && u.slug !== DEMO_SLUG;
            const onClickTile = locked ? startGoogle : () => nav(`/universities/${u.slug}`);
            return (
              <div
                key={u.slug}
                onClick={onClickTile}
                title={locked ? "Sign in to view" : undefined}
                className={`relative card overflow-hidden cursor-pointer transition-all hover:-translate-y-0.5 hover:border-[var(--s300)] ${
                  locked ? "opacity-60 hover:opacity-100" : ""
                }`}
                data-testid={`uni-${u.slug}`}
              >
                <UniversityImage
                  slug={u.slug}
                  name={u.name}
                  aspectClass="aspect-[16/9]"
                  rounded=""
                  showGradientOverlay
                />
                <div className="p-6">
                  {locked && (
                    <div className="absolute top-3 right-3 flex items-center gap-1.5 text-micro font-semibold tracking-[0.05em] uppercase text-[var(--s500)] bg-white/90 backdrop-blur border border-[var(--s200)] px-2 py-1 rounded-[var(--r8)]">
                      <Lock size={11} strokeWidth={2.2} /> Sign in
                    </div>
                  )}
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-h3 font-semibold text-[var(--s900)] leading-snug">{u.name}</div>
                      <div className="text-label text-[var(--s500)] mt-1">{u.city} · {u.country}</div>
                    </div>
                    {!locked && (
                      <span className="text-micro font-semibold tracking-[0.05em] text-white bg-[var(--ind)] border border-[var(--ind)] px-2 py-1 rounded-[var(--r8)] whitespace-nowrap">
                        QS #{u.qs_rank_2025}
                      </span>
                    )}
                  </div>
                  <p className={`text-body-sm leading-relaxed text-[var(--s600)] mt-4 line-clamp-2 ${locked ? "blur-sm select-none" : ""}`}>{u.summary}</p>
                  <div className="mt-5 pt-4 border-t border-[var(--s200)] grid grid-cols-3 gap-3">
                    <Bit k="Tuition/yr" v={fmtINR(u.tuition_per_year_inr)} locked={locked} />
                    <Bit k="IB med." v={`${u.ib_score_median}/45`} locked={locked} />
                    <Bit k="PSW" v={`${u.post_study_work_years} yr`} locked={locked} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {items.length === 0 && (
          <div className="text-center py-20 text-[var(--s500)] text-h3">
            No universities match these filters.
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}

function Bit({ k, v, locked }) {
  return (
    <div>
      <div className="text-micro font-semibold tracking-[0.05em] uppercase text-[var(--s400)]">{k}</div>
      <div className={`text-body-sm font-medium text-[var(--s900)] mt-0.5 ${locked ? "blur-sm select-none" : ""}`}>{v}</div>
    </div>
  );
}

function Pill({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`text-body-sm px-3 py-1.5 rounded-[var(--r8)] border transition-colors inline-flex items-center gap-1.5 ${
        active
          ? "bg-[var(--ind)] text-white border-[var(--ind)]"
          : "bg-white text-[var(--s700)] border-[var(--s200)] hover:border-[var(--s400)]"
      }`}
    >
      {children}
    </button>
  );
}

// ISO 3166-1 alpha-2 mapping for codes that differ from our internal codes
// (we use "UK" but the ISO code is "GB" — flagcdn expects ISO).
const FLAG_ISO_OVERRIDE = { UK: "gb" };

function FlagIcon({ code }) {
  const iso = (FLAG_ISO_OVERRIDE[code] || code).toLowerCase();
  return (
    <img
      src={`https://flagcdn.com/24x18/${iso}.png`}
      srcSet={`https://flagcdn.com/24x18/${iso}.png 1x, https://flagcdn.com/48x36/${iso}.png 2x`}
      width="20"
      height="15"
      alt=""
      aria-hidden
      loading="lazy"
      className="inline-block rounded-[2px] shadow-[0_0_0_1px_rgba(0,0,0,0.06)] shrink-0"
    />
  );
}
